import 'dayjs/locale/ru'
import 'dayjs/locale/kk'

import dayjs from 'dayjs'
import { FC, useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import WheelPicker, { WheelOption } from './WheelPicker'

interface Props {
  value: Date | null
  onChange: (date: Date) => void
  maxDate?: Date
}

function buildDateOptions(maxDate: Date | undefined, lang: string): WheelOption[] {
  const today = dayjs().startOf('day')
  const last = maxDate ? dayjs(maxDate).startOf('day') : today.add(3, 'day')
  const opts: WheelOption[] = []
  let cur = today

  while (!cur.isAfter(last)) {
    const val = cur.format('YYYY-MM-DD')
    let label: string
    if (cur.isSame(today, 'day')) {
      label = lang === 'kk' ? 'Бүгін' : 'Сегодня'
    } else if (cur.isSame(today.add(1, 'day'), 'day')) {
      label = lang === 'kk' ? 'Ертең' : 'Завтра'
    } else {
      label = cur.locale(lang === 'kk' ? 'kk' : 'ru').format('D MMM')
    }
    opts.push({ value: val, label })
    cur = cur.add(1, 'day')
  }
  return opts
}

function buildHourOptions(dateStr: string): WheelOption[] {
  const now = dayjs()
  const isToday = dateStr === now.format('YYYY-MM-DD')
  const opts: WheelOption[] = []

  for (let h = 0; h < 24; h++) {
    if (isToday && h < now.hour()) continue
    const label = String(h).padStart(2, '0')
    opts.push({ value: label, label })
  }
  return opts
}

function buildMinuteOptions(dateStr: string, hourStr: string): WheelOption[] {
  const now = dayjs()
  const isToday = dateStr === now.format('YYYY-MM-DD')
  const isCurrentHour = isToday && parseInt(hourStr) === now.hour()
  const opts: WheelOption[] = []

  for (let m = 0; m < 60; m++) {
    if (isCurrentHour && m <= now.minute()) continue
    const label = String(m).padStart(2, '0')
    opts.push({ value: label, label })
  }
  return opts
}

function getFirstValid(dateOptions: WheelOption[]): { date: string; hour: string; minute: string } {
  for (const d of dateOptions) {
    const hours = buildHourOptions(d.value)
    for (const h of hours) {
      const minutes = buildMinuteOptions(d.value, h.value)
      if (minutes.length > 0) return { date: d.value, hour: h.value, minute: minutes[0].value }
    }
  }
  const fallback = dateOptions[0]?.value ?? dayjs().format('YYYY-MM-DD')
  return { date: fallback, hour: '09', minute: '00' }
}

const IOSDateTimePicker: FC<Props> = ({ value, onChange, maxDate }) => {
  const { i18n } = useTranslation()

  const dateOptions = useMemo(() => buildDateOptions(maxDate, i18n.language), [maxDate, i18n.language])

  const initial = useMemo(() => {
    if (value) {
      const d = dayjs(value)
      return { date: d.format('YYYY-MM-DD'), hour: d.format('HH'), minute: d.format('mm') }
    }
    return getFirstValid(dateOptions)
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const [selDate, setSelDate] = useState(initial.date)
  const [selHour, setSelHour] = useState(initial.hour)
  const [selMinute, setSelMinute] = useState(initial.minute)

  const hourOptions = useMemo(() => buildHourOptions(selDate), [selDate])
  const minuteOptions = useMemo(() => buildMinuteOptions(selDate, selHour), [selDate, selHour])

  // Emit on mount so parent's dateOfGift is set immediately
  useEffect(() => {
    onChange(dayjs(`${initial.date} ${initial.hour}:${initial.minute}`).toDate())
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // Sync from external value changes
  useEffect(() => {
    if (!value) return
    const d = dayjs(value)
    setSelDate(d.format('YYYY-MM-DD'))
    setSelHour(d.format('HH'))
    setSelMinute(d.format('mm'))
  }, [value])

  const handleDateChange = useCallback(
    (newDate: string) => {
      const hours = buildHourOptions(newDate)
      const validHour = hours.some(h => h.value === selHour) ? selHour : hours[0]?.value ?? '09'
      const minutes = buildMinuteOptions(newDate, validHour)
      const validMinute = minutes.some(m => m.value === selMinute) ? selMinute : minutes[0]?.value ?? '00'
      setSelDate(newDate)
      setSelHour(validHour)
      setSelMinute(validMinute)
      onChange(dayjs(`${newDate} ${validHour}:${validMinute}`).toDate())
    },
    [selHour, selMinute, onChange]
  )

  const handleHourChange = useCallback(
    (newHour: string) => {
      const minutes = buildMinuteOptions(selDate, newHour)
      const validMinute = minutes.some(m => m.value === selMinute) ? selMinute : minutes[0]?.value ?? '00'
      setSelHour(newHour)
      setSelMinute(validMinute)
      onChange(dayjs(`${selDate} ${newHour}:${validMinute}`).toDate())
    },
    [selDate, selMinute, onChange]
  )

  const handleMinuteChange = useCallback(
    (newMinute: string) => {
      setSelMinute(newMinute)
      onChange(dayjs(`${selDate} ${selHour}:${newMinute}`).toDate())
    },
    [selDate, selHour, onChange]
  )

  const divider = (
    <div style={{ width: 1, backgroundColor: 'rgba(120,120,128,0.18)', flexShrink: 0, margin: '12px 0' }} />
  )

  return (
    <div
      style={{
        display: 'flex',
        backgroundColor: 'var(--bg-input)',
        borderRadius: 14,
        overflow: 'hidden',
        userSelect: 'none'
      }}
    >
      <WheelPicker options={dateOptions} value={selDate} onChange={handleDateChange} />
      {divider}
      <WheelPicker options={hourOptions} value={selHour} onChange={handleHourChange} />
      {/* Colon separator */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          padding: '0 2px',
          color: 'var(--text-primary)',
          fontWeight: 600,
          fontSize: 16,
          flexShrink: 0
        }}
      >
        :
      </div>
      <WheelPicker options={minuteOptions} value={selMinute} onChange={handleMinuteChange} />
    </div>
  )
}

export default IOSDateTimePicker
